import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Upload, CheckCircle, MessageCircle, Link2, X, FileText } from 'lucide-react';
import { getShareLink, todayDDMM, shortId } from '../utils/links';
import SlideFormShell from '../components/SlideFormShell';
import SlideOptionRow from '../components/SlideOptionRow';
import { requireAuth } from '../components/AuthGate';

const HARDSHIP_DIMENSIONS = [
  {
    title: 'Dimension A: Housing Infrastructure',
    items: [
      { id: 'a1', label: 'HDB 1–2 room rental flat' },
      { id: 'a2', label: 'Rental/utility arrears (≥2 months unpaid)' },
      { id: 'a3', label: 'Poor living conditions (Structural/Sanitary issues)' },
      { id: 'a4', label: 'Overcrowding (≥3 persons per bedroom room)' }
    ]
  },
  {
    title: 'Dimension B: Family Crisis',
    items: [
      { id: 'b1', label: 'Primary caregiver, child under 6, no childcare support' },
      { id: 'b2', label: 'Spouse currently incarcerated' },
      { id: 'b3', label: 'Lack of Family Support or Estranged directly' },
      { id: 'b4', label: 'Recent Job Displacement (Lost job within last 6 months)' }
    ]
  },
  {
    title: 'Dimension C: Medical Limitations',
    items: [
      { id: 'c1', label: 'Certified medically unfit for work (long-term/disability)' },
      { id: 'c2', label: 'Dedicated Full-time Caregiver to a dependent' },
      { id: 'c3', label: 'Applicant or spouse is a Senior Citizen (Age 65+)' }
    ]
  },
  {
    title: 'Dimension D: Debt & External Pressure',
    items: [
      { id: 'd1', label: 'Receiving absolute NO assistance from external orgs (SSO, etc)' },
      { id: 'd2', label: 'Gross household income is under strictly ≤$2,500' },
      { id: 'd3', label: 'LTVP / STVP pass holder with strict employment restrictions' }
    ]
  }
];

const PCI_OPTIONS = [
  { value: 'tier1', label: 'Less than $300', hint: 'Tier 1: Fakir' },
  { value: 'tier2', label: '$301 – $400', hint: 'Tier 2: Miskin 1' },
  { value: 'tier3', label: '$401 – $600', hint: 'Tier 3: Miskin 2' },
  { value: 'tier4', label: '$601 – $800', hint: 'Tier 4: Exceptional Setup' },
  { value: 'tier5', label: 'Above $800', hint: '' }
];

export default function RequestAssistance({ onBack, onViewTracker }) {
  const fileInputRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  // Form state
  const [journey, setJourney] = useState('new');
  const [fullName, setFullName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [nric, setNric] = useState('');
  const [pci, setPci] = useState('');
  const [hardship, setHardship] = useState({});
  const [files, setFiles] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [declaration, setDeclaration] = useState(false);

  const isValidSGMobile = (num) => /^[89]\d{7}$/.test(num);
  const formatMobileDisplay = (num) => (num ? `+65 ${num.slice(0, 4)} ${num.slice(4)}`.trim() : '');

  const handleMobileChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    setMobileNumber(digits);
  };

  const toggleHardship = (id) => setHardship((h) => ({ ...h, [id]: !h[id] }));

  const handleFileSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    const combined = [...files, ...picked].slice(0, 5);
    setFiles(combined);
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const completeAssessment = () => {
    const trackingLink = getShareLink('assist-track', {
      ddmm: todayDDMM(),
      id: shortId()
    });

    setSubmittedData({
      mobileNumber,
      trackingLink,
      fullName,
      submittedAt: new Date()
    });
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShareWhatsApp = () => {
    if (!submittedData) return;
    const text = encodeURIComponent(
      `Assalamualaikum,\n\nI've just submitted a Financial Assistance application under IhsanOne. Track my application status here:\n\n${submittedData.trackingLink}\n\nMay Allah grant ease. Ameen.`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  // Success screen
  if (submitted && submittedData) {
    return (
      <div className="min-h-screen bg-muted/20">
        <header className="bg-card border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button variant="ghost" onClick={onBack} className="gap-2" data-testid="assist-success-back">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="text-lg sm:text-xl font-semibold">Application Submitted</h1>
            <div className="w-16" />
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="text-center">
            <CardContent className="p-8 sm:p-12">
              <div className="w-20 h-20 bg-[#5EEAD4]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-12 w-12 text-primary" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold mb-4">Application Submitted</h2>
              <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                Our Triage Engine is processing your request. An official representative will contact you shortly, and you will receive a secure live tracking link via WhatsApp at{' '}
                <span className="font-semibold text-foreground">{formatMobileDisplay(submittedData.mobileNumber)}</span>{' '}
                to monitor your application status.
              </p>

              <div className="bg-[#5EEAD4]/10 border border-[#5EEAD4]/40 p-4 rounded-xl mb-6 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <p className="text-sm font-semibold">Your tracking link</p>
                </div>
                <code
                  data-testid="assist-tracking-link"
                  className="text-xs sm:text-sm bg-background px-3 py-2 rounded border block break-all text-muted-foreground"
                >
                  {submittedData.trackingLink}
                </code>
              </div>

              <div className="flex flex-col gap-3">
                <Button
                  data-testid="assist-view-tracker"
                  onClick={() => onViewTracker?.(submittedData)}
                  size="lg"
                  className="h-12 text-base gap-2"
                >
                  <Link2 className="h-4 w-4" />
                  View Live Application Tracker
                </Button>
                <Button
                  data-testid="assist-share-whatsapp"
                  onClick={handleShareWhatsApp}
                  size="lg"
                  className="h-12 text-base gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white"
                >
                  <MessageCircle className="h-4 w-4" />
                  Share via WhatsApp
                </Button>
                <Button
                  onClick={onBack}
                  variant="outline"
                  size="lg"
                  className="h-12 text-base"
                >
                  Return to Services
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // One question per screen — friendlier than one long scrolling form.
  const steps = [
    {
      id: 'journey',
      hero: 'journey',
      title: 'Are you new here?',
      subtitle: 'This helps us route your application correctly.',
      render: () => (
        <div className="space-y-3">
          <SlideOptionRow
            testId="journey-new"
            selected={journey === 'new'}
            onClick={() => setJourney('new')}
            title="New Applicant"
            subtitle="This is my first time applying"
          />
          <SlideOptionRow
            testId="journey-repeat"
            selected={journey === 'repeat'}
            onClick={() => setJourney('repeat')}
            title="Repeat / Renewal Applicant"
            subtitle="I've applied for assistance before"
          />
        </div>
      )
    },
    {
      id: 'full-name',
      hero: 'name',
      title: "What's your full name?",
      subtitle: 'As it appears on your NRIC.',
      isValid: () => fullName.trim().length > 0,
      render: () => (
        <input
          data-testid="input-full-name"
          autoFocus
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Enter full name as on NRIC"
          className="h-12 w-full rounded-xl bg-muted/60 px-4 text-base focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      )
    },
    {
      id: 'mobile',
      hero: 'phone',
      title: "What's your mobile number?",
      subtitle: 'Required to receive your WhatsApp application tracking link.',
      isValid: () => isValidSGMobile(mobileNumber),
      render: () => (
        <div>
          <div
            className={`flex h-12 items-center overflow-hidden rounded-xl bg-muted/60 focus-within:ring-2 focus-within:ring-primary/40 ${
              mobileNumber && !isValidSGMobile(mobileNumber) ? 'ring-2 ring-destructive/60' : ''
            }`}
          >
            <span className="flex h-full flex-shrink-0 items-center gap-1 px-3 text-sm font-medium">🇸🇬 +65</span>
            <input
              data-testid="input-assist-mobile"
              autoFocus
              type="tel"
              inputMode="numeric"
              maxLength={8}
              placeholder="8123 4567"
              value={mobileNumber}
              onChange={handleMobileChange}
              className="h-full flex-1 bg-transparent pr-3 text-base focus:outline-none"
            />
          </div>
          {mobileNumber && !isValidSGMobile(mobileNumber) && (
            <p className="mt-1.5 text-xs text-destructive">Enter a valid 8-digit Singapore mobile starting with 8 or 9.</p>
          )}
        </div>
      )
    },
    {
      id: 'nric',
      hero: 'id',
      title: "What's your NRIC / FIN?",
      subtitle: 'Used to verify your identity for the application.',
      isValid: () => nric.trim().length > 0,
      render: () => (
        <input
          data-testid="input-nric"
          autoFocus
          value={nric}
          onChange={(e) => setNric(e.target.value.toUpperCase())}
          maxLength={9}
          placeholder="e.g. S1234567A"
          className="h-12 w-full rounded-xl bg-muted/60 px-4 text-base uppercase focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      )
    },
    {
      id: 'pci',
      hero: 'money',
      title: "What's your PCI range?",
      subtitle: 'Per Capita Income = total household income ÷ household members.',
      isValid: () => !!pci,
      render: () => (
        <div className="space-y-2.5">
          {PCI_OPTIONS.map((opt) => (
            <SlideOptionRow
              key={opt.value}
              testId={`pci-${opt.value}`}
              selected={pci === opt.value}
              onClick={() => setPci(opt.value)}
              title={opt.label}
              subtitle={opt.hint}
            />
          ))}
        </div>
      )
    },
    {
      id: 'hardship',
      hero: 'home',
      title: 'Any of these apply to you?',
      subtitle: 'Optional — select all that currently apply to your situation.',
      render: () => (
        <div className="space-y-4">
          {HARDSHIP_DIMENSIONS.map((dim) => (
            <div key={dim.title} className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{dim.title}</p>
              <div className="space-y-2">
                {dim.items.map((item) => (
                  <label
                    key={item.id}
                    htmlFor={`hs-${item.id}`}
                    className="flex items-start gap-3 rounded-lg border border-border/60 p-2.5 transition-colors hover:bg-muted/40"
                  >
                    <Checkbox
                      id={`hs-${item.id}`}
                      data-testid={`hs-${item.id}`}
                      checked={!!hardship[item.id]}
                      onCheckedChange={() => toggleHardship(item.id)}
                      className="mt-0.5"
                    />
                    <span className="text-sm leading-relaxed">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )
    },
    {
      id: 'documents',
      hero: 'documents',
      title: 'Any documents to share?',
      subtitle: 'Optional — bills, doctor memos, pay-slips, or ICs (max 5 files).',
      render: () => (
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-dropzone"
            className="w-full rounded-xl border-2 border-dashed border-primary/40 p-6 text-center transition-colors hover:bg-primary/5"
          >
            <Upload className="mx-auto mb-2 h-6 w-6 text-primary" />
            <p className="text-sm font-medium text-foreground">Tap here to select files or photos</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Maximum 5 files. PNG, JPG, PDF.</p>
          </button>
          {files.length > 0 && (
            <ul className="space-y-1.5 pt-2" data-testid="file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm">
                  <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive" aria-label="Remove file">
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )
    },
    {
      id: 'remarks',
      hero: 'notes',
      title: 'Anything else to share?',
      subtitle: 'Optional — any additional context for your caseworker.',
      render: () => (
        <textarea
          data-testid="input-remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={5}
          placeholder="Share any additional context that will help your assessment..."
          className="w-full rounded-xl bg-muted/60 p-4 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      )
    },
    {
      id: 'declaration',
      hero: 'declaration',
      title: 'Final step: your declaration',
      subtitle: 'Please confirm to submit your application.',
      isValid: () => declaration,
      render: () => (
        <label
          htmlFor="declaration"
          className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-muted/20 p-4"
        >
          <Checkbox
            id="declaration"
            data-testid="declaration-checkbox"
            checked={declaration}
            onCheckedChange={(v) => setDeclaration(!!v)}
            className="mt-0.5"
          />
          <span className="text-xs leading-relaxed sm:text-sm">
            I declare that this application is truthful and authorize the retrieval of information from relevant databases for triage processing. I am aware that concealing material facts is an offense.
          </span>
        </label>
      )
    }
  ];

  return (
    <SlideFormShell
      testId="assist-flow"
      steps={steps}
      onCancel={onBack}
      finishLabel="Process Assessment"
      finishMorphLabel="Submitted"
      onBeforeFinish={requireAuth}
      onFinish={completeAssessment}
    />
  );
}
